import React, { useEffect, useState, useRef } from "react";
import {
  Box, TextField, Typography, List, ListItem, ListItemButton,
  ListItemText, Divider, Paper
} from "@mui/material";
import {
  MapContainer, TileLayer, Marker, Popup, useMap
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";

// Icône verte personnalisée
const greenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const FlyToLocation = ({ latlng }) => {
  const map = useMap();
  useEffect(() => {
    if (latlng) {
      map.flyTo(latlng, 14);
    }
  }, [latlng, map]);
  return null;
};

const geocodeAddress = async (address) => {
  const full = `${address.nr_voie || ""} ${address.type_voie || ""} ${address.voie || ""}, ${address.code_postal || ""} ${address.ville || ""}, ${address.pays || "France"}`;
  try {
    const res = await fetch(`${process.env.REACT_APP_VENDOR_SYB_API}/geocode?q=${encodeURIComponent(full)}`);
    const data = await res.json();
    if (data.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch (err) {
    console.warn("Erreur géocodage :", err);
  }
  return null;
};

const MapPartenaires = ({ rawSites }) => {
  const { t } = useTranslation("partenaires/MapPartenaires");

  const [sites, setSites] = useState([]);
  const [search, setSearch] = useState("");
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);

  // refs pour chaque item + le conteneur scrollable
  const siteRefs = useRef({});
  const listRef = useRef(null);

  useEffect(() => {
    if (rawSites.length > 0) {
      setSites(rawSites);
      setFilteredSites(rawSites);
    }
  }, [rawSites]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFilteredSites(
      sites.filter((site) => {
        const company = site.Company?.name?.toLowerCase() || "";
        const address = `${site.SiteAddress?.nr_voie || ""} ${site.SiteAddress?.type_voie || ""} ${site.SiteAddress?.voie || ""} ${site.SiteAddress?.code_postal || ""} ${site.SiteAddress?.ville || ""}`.toLowerCase();
        return company.includes(q) || address.includes(q);
      })
    );
  }, [search, sites]);

  // Scroll contrôlé dans le conteneur de la liste (évite le scroll de la page)
  useEffect(() => {
    const el = selectedSite && siteRefs.current[selectedSite.id];
    const container = listRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const current = container.scrollTop;
      const delta = (elRect.top - containerRect.top) - (container.clientHeight - el.clientHeight) / 2;
      const target = current + delta;
      container.scrollTo({ top: Math.max(target, 0), behavior: "smooth" });
    }
  }, [selectedSite]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 8 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "1200px",
          borderRadius: "20px",
          px: { xs: 3, md: 4 },
          py: { xs: 4, md: 5 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
        }}
      >
        {/* Liste */}
        <Box sx={{
          width: { xs: "100%", md: "35%" },
          maxHeight: "600px",
          overflowY: "auto",
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          p: 2,
          boxShadow: "inset 0 0 4px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0"
        }} ref={listRef}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.3rem",
              color: "#449F2D",
              mb: 2,
            }}
          >
            {t("title")}
          </Typography>
          <TextField
            fullWidth
            placeholder={t("searchPlaceholder")}
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <List dense>
            {filteredSites.map((site, i) => (
              <div key={i} ref={(el) => (siteRefs.current[site.id] = el)}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => setSelectedSite(site)}
                    sx={{
                      "&:hover": { backgroundColor: "#F0F0F0" },
                      borderRadius: "8px",
                      backgroundColor: selectedSite?.id === site.id ? "#4CAF50" : "transparent",
                      color: selectedSite?.id === site.id ? "#fff" : "inherit",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600 }}>
                          {site.Company?.name || t("unknownCompany")}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: selectedSite?.id === site.id ? "#fff" : "#555" }}>
                          {`${site.SiteAddress?.nr_voie || ""} ${site.SiteAddress?.type_voie || ""} ${site.SiteAddress?.voie || ""}, ${site.SiteAddress?.code_postal || ""} ${site.SiteAddress?.ville || ""}`}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ my: 1 }} />
              </div>
            ))}
            {filteredSites.length === 0 && (
              <Typography textAlign="center" mt={2}>
                {t("noResults")}
              </Typography>
            )}
          </List>
        </Box>

        {/* Carte */}
        <Box sx={{ flexGrow: 1, borderRadius: "16px", overflow: "hidden" }}>
          <MapContainer
            center={[46.5, 2.2]}
            zoom={6}
            scrollWheelZoom
            style={{ height: "600px", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {selectedSite?.latlng && <FlyToLocation latlng={selectedSite.latlng} />}
            <MarkerClusterGroup>
              {filteredSites.map((site, i) => (
                <Marker
                  key={i}
                  position={site.latlng}
                  icon={greenIcon}
                  eventHandlers={{
                    click: () => setSelectedSite(site),
                  }}
                >
                  <Popup>
                    <strong>{site.Company?.name || t("unknownCompany")}</strong><br />
                    {site.SiteAddress?.nr_voie} {site.SiteAddress?.type_voie} {site.SiteAddress?.voie}<br />
                    {site.SiteAddress?.code_postal} {site.SiteAddress?.ville}
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          </MapContainer>
        </Box>
      </Paper>
    </Box>
  );
};

export default MapPartenaires;